// StatisticReport3.tsx
import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import axios from '../../../api/axiosSetup3.ts';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../hooks/useFetch2.ts';
import { Group } from '../../../types/group.ts';
import { EntityPath } from '../../../types/plate.ts';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3.tsx';
import i18n from '../../../i18n.ts';
import dayjs, { Dayjs } from "dayjs";
import { LocalizedDatePicker } from '../../localizedDatePicker/LocalizedDatePicker.tsx';
import { Subtenant } from '../../../api/subtenantApi.ts';

interface EventCount {
    group_id: number | null;
    entity_id: number;
    cnt: number;
}

interface Data {
    res_group?: Group[];
    res_entity?: EntityPath[];
    res_event?: EventCount[];
    getSubtenantNameByGroupId: (group_id: number) => string;
}

const defaultData: Data = {
    res_group: [],
    res_entity: [],
    res_event: [],
    getSubtenantNameByGroupId: (group_id: number) => ''
};

interface SubTenantToGrop {
    customer_id: number;
    subtenant_id: number;
    group_id: number;
}

const getReport = async (params: { from: string; to: string }) => {
    const { data: res_subtenant } = await axios.get<Subtenant[]>(`/lpr_subtenant`);
    const { data: res_stg_temp } = await axios.get<SubTenantToGrop[]>(`/lpr_subtenant_to_group`);
    const { data: res_group } = await axios.get<Group[]>(`/lpr_group`);
    const { data: res_entity } = await axios.get<EntityPath[]>(`/v_entity_path`);
    const { data: res_event } = await axios.post<EventCount[]>(`/rpc/sf_get_event_stats_all`, {
        from_date: params.from,
        to_date: params.to
    });
    
    const subtenantMap = new Map<number, string>(
        res_subtenant.map(s => [s.subtenant_id, s.name])
    );

    const groupToSubtenantMap = new Map<number, number>(
        res_stg_temp.map(stg => [stg.group_id, stg.subtenant_id])
    );

    const getSubtenantNameByGroupId = (groupId: number): string => {
        const subtenantId = groupToSubtenantMap.get(groupId);
        if (!subtenantId) return '';
        
        return subtenantMap.get(subtenantId) || '';
    };    
    return { getSubtenantNameByGroupId, res_group, res_entity, res_event };
}

// Вспомогательная функция для получения списка всех ключей колонок (ID групп + "Other" при необходимости)
const getGroupColumnsKeys = (res_group: Group[], hasNullGroup: boolean): string[] => {
    const keys = res_group.map(g => String(g.group_id));
    if (hasNullGroup) {
        keys.push('other');
    }
    return keys;
}

const calculateSum = (res_entity: EntityPath[], columnKeys: string[], idx: number, idx1: number) => {
    columnKeys.forEach(col => {
        res_entity[idx][col] = (res_entity[idx][col] || 0) + (res_entity[idx1][col] || 0);
    });
}

// recursive calculation
const calculateTotal = (res_entity: EntityPath[], columnKeys: string[], idx: number) => {
    if (res_entity.length === 0) return;

    if (res_entity[idx].type === 3) return; // уже посчитано

    for (let idx1 = 0; idx1 < res_entity.length; idx1++) {
        const entity = res_entity[idx1];
        
        if (entity.parent_id === res_entity[idx].entity_id) {
            calculateTotal(res_entity, columnKeys, idx1);
            calculateSum(res_entity, columnKeys, idx, idx1);
        }
    }
}

const addPrefix = (res_entity: EntityPath[]) => {
    const prefix = (type: number) => ''.padEnd(type * 4, ' ');
    for (let idx1 = 0; idx1 < res_entity.length; idx1++) {
        res_entity[idx1].name = prefix(res_entity[idx1].type) + res_entity[idx1].name;
    }
}

// pivot
const transformToRows = (
    res_group: Group[],
    res_entity: EntityPath[],   
    res_event: EventCount[]
) => {
    const sortedEntity = [...res_entity].sort((a, b) => a.path_names.localeCompare(b.path_names));
    
    // Проверяем, есть ли события без группы (group_id == null)
    const hasNullGroup = res_event.some(e => e.group_id === null || e.group_id === undefined);
    const columnKeys = getGroupColumnsKeys(res_group, hasNullGroup);

    // 1. Создаем быстрый индекс для поиска событий: "entityId_groupId" -> cnt
    const eventLookup = res_event.reduce((acc, event) => {
        const groupIdKey = (event.group_id === null || event.group_id === undefined) ? 'other' : event.group_id;
        const key = `${event.entity_id}_${groupIdKey}`;
        acc[key] = (acc[key] || 0) + event.cnt;
        return acc;
    }, {} as Record<string, number>);

    // 2. Превращаем каждую сущность в строку таблицы
    const res1 = sortedEntity.map((entity) => {
        const row: any = {
            id: entity.entity_id,     // Обязательный ID для DataGrid
            name: entity.name,        // Поле для колонки 'name'
            type: entity.type,        // Поле для колонки 'type'
            entity_id: entity.entity_id,
            parent_id: entity.parent_id,
        };

        // 3. Динамически добавляем колонки групп и колонку Other
        columnKeys.forEach((colKey) => {
            const lookupKey = `${entity.entity_id}_${colKey}`;
            row[colKey] = eventLookup[lookupKey] || 0;
        });

        return row;
    });

    const root = res1.findIndex((entity) => entity.type === 0);
    if (root < 0) return [];

    calculateTotal(res1, columnKeys, root);
    addPrefix(res1);
    return res1;
};

export const StatisticReport: React.FC = () => {
    const { t } = useTranslation();

    const [from, setFrom] = useState<Dayjs | null>(dayjs().startOf("day"));
    const [to, setTo] = useState<Dayjs | null>(dayjs().endOf("day"));

    const { data, setData, error, loading, update } = useFetch<Data>(
        getReport,
        defaultData, 
        { from: from?.toISOString(), to: to?.toISOString() }
    );
    const { res_group = [], res_entity = [], res_event = [] } = data || {};

    const columns0: GridColDef[] = [
        { field: "name", headerName: t('StatisticReport.entity_name'), type: "string", width: 150 },
    ];

    const res_group_sorted = [...res_group].sort((a: Group, b: Group): number => {
        const nameA = data.getSubtenantNameByGroupId(a.group_id) || '';
        const nameB = data.getSubtenantNameByGroupId(b.group_id) || '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });

    const columns1: GridColDef[] = res_group_sorted.map((group) => ({
        field: String(group.group_id),
        headerName: group.name,
        type: "number",
        width: 100,
        renderHeader: () => (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2rem', padding: '4px 0' }}>
                <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 'normal' }}>
                    {data.getSubtenantNameByGroupId(group.group_id) || '--'}
                </span>
                <span style={{ fontWeight: 'bold' }}>
                    {group.name}
                </span>
            </div>
        ),
    }));

    // Проверяем наличие событий с group_id === null
    const hasNullGroup = useMemo(() => {
        return res_event.some(e => e.group_id === null || e.group_id === undefined);
    }, [res_event]);

    // Если есть такие события, добавляем колонку "Other" в конец
    if (hasNullGroup) {
        columns1.push({
            field: "other",
            headerName: t('StatisticReport.other', 'Other'),
            type: "number",
            width: 100,
            renderHeader: () => (
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2rem', padding: '4px 0' }}>
                    <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 'normal' }}>
                        --
                    </span>
                    <span style={{ fontWeight: 'bold' }}>
                        {t('StatisticReport.other', 'Other')}
                    </span>
                </div>
            )
        });
    }

    const columns = [...columns0, ...columns1];

    const rows = useMemo(() => transformToRows(
        res_group,
        res_entity,
        res_event
    ), [res_group, res_entity, res_event]);

    const handleRefresh = () => {
        update({ from: from?.toISOString(), to: to?.toISOString() });
    };

    return (
        <Paper sx={{ p: 2, maxWidth: 1600, mx: "auto", mt: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="body1">
                    {t('StatisticReport.Event statistics_for_the_last_10_days')}
                </Typography>
            </Box>

            <Stack 
                direction="row"
                spacing={2} 
                alignItems="center"
                mb={3}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 20 }}>{t('EventList.from')}</Typography>
                    <LocalizedDatePicker
                        value={from}
                        onChange={(v) => setFrom(v)}
                    />
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 20 }}>{t('EventList.to')}</Typography>
                    <LocalizedDatePicker
                        value={to}
                        onChange={(v) => setTo(v)}
                    />
                </Stack>
                <Button
                    variant="contained"
                    onClick={handleRefresh}
                >
                    {t('EventList.refresh')}
                </Button>            
            </Stack>

            <LocalizedGrid
                lang={i18n.language as any}
                gridId='StatisticReport2'
                rows={rows}
                columns={columns} 
                getRowId={(row) => row.entity_id}
                loading={loading}
                disableColumnMenu={true}
                disableColumnSorting={true}
                sx={{ 
                    maxWidth: '1600px',
                    '& .MuiDataGrid-cell': {
                        whiteSpace: 'pre',
                    },
                    '& .MuiTablePagination-actions': { display: 'none' },
                    '& .MuiTablePagination-displayedRows': { display: 'none' },
                    '& .MuiTablePagination-selectLabel': { display: 'none' },
                    '& .MuiTablePagination-input': { display: 'none' },
                    '& .MuiTablePagination-spacer': { display: 'none' },
                    '& .MuiTablePagination-toolbar': { justifyContent: 'flex-end' },
                }}
                hideToolbar={true}
            />

            <Typography color='error'>{error?.message}</Typography>
        </Paper>
    );
}